import { useParams, Link, useLocation } from 'react-router-dom';
import { useSingleEvent } from '../hooks/useSingleEvent';
import { extractImages } from '../lib/parser';
import { Check, Copy, Download, ExternalLink, Info, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { Event, nip19 } from 'nostr-tools';

export function ImagePage() {
    const { noteId, index } = useParams<{ noteId: string; index: string }>();
    const location = useLocation();

    // 1. Try to get event from router state (passed from MediaGrid) OR LocalStorage (passed from New Tab link)
    const initialEvent = (location.state?.event as Event | undefined) || (() => {
        try {
            let hexId = noteId || '';
            if (hexId.startsWith('note1')) {
                try {
                    const decoded = nip19.decode(hexId);
                    if (decoded.type === 'note') {
                        hexId = decoded.data as string;
                    }
                } catch (e) {
                    console.error('Failed to decode note ID for cache lookup', e);
                }
            }

            const cached = localStorage.getItem(`g_evt_${hexId}`)
            if (cached) {
                return JSON.parse(cached) as Event
            }
        } catch (e) {
            return undefined
        }
    })();

    // 2. Fallback to hook if state is missing
    const { event: fetchedEvent, isLoading, error } = useSingleEvent(initialEvent ? undefined : noteId);

    const event = initialEvent || fetchedEvent;
    const [copied, setCopied] = useState(false);

    const imageIndex = parseInt(index || '0', 10);
    const images = event ? extractImages(event.content) : [];
    const imageUrl = images[imageIndex];

    const currentUrl = window.location.href;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(currentUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownload = async () => {
        if (!imageUrl) return;
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `mavililer-img-${noteId}-${imageIndex}.jpg`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            console.error('Download failed', e);
            window.open(imageUrl, '_blank');
        }
    };

    // Loading State
    if (!event && isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-surface-950">
                <div className="w-8 h-8 border-2 border-mavi-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Error State
    if ((!event && !isLoading && error) || (event && !imageUrl)) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-surface-950 text-white p-4 text-center">
                <AlertCircle className="w-12 h-12 text-kirmizi-500 mb-4" />
                <p className="text-lg font-poppins mb-2 text-white">Görsel yüklenemedi.</p>
                <p className="text-sm text-gray-400 mb-6">Bu görsel silinmiş veya bulunamıyor olabilir.</p>

                <Link to="/" className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors font-medium">
                    Ana Sayfaya Dön
                </Link>
            </div>
        );
    }

    // Success State
    return (
        <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center relative overflow-hidden">

            {/* Main Image Area */}
            <div className="flex-1 w-full h-full flex items-center justify-center p-4">
                <img
                    src={imageUrl}
                    alt="Entry Media"
                    className="max-w-full max-h-[calc(100vh-100px)] object-contain shadow-2xl rounded-lg"
                />
            </div>

            {/* Footer Bar */}
            <div className="w-full bg-surface-900/90 backdrop-blur-md border-t border-white/5 p-4 z-10">
                <div className="max-w-[1920px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">

                    {/* Left: Info Text */}
                    <div className="flex items-center gap-3 text-xs md:text-sm text-gray-400 font-poppins">
                        <Info className="w-4 h-4 shrink-0 text-mavi-400" />
                        <p>
                            Bu görsel topluluk üyelerimiz tarafından paylaşılmıştır. Yasal olmayan bir içerik olduğunu düşünüyorsanız <Link
                                to="/contact"
                                state={{
                                    subject: 'Hatalı/Yasadışı Görsel Bildirimi',
                                    description: `Şikayet edilen görsel adresi: ${currentUrl}\n\nŞikayet nedeni:`
                                }}
                                className="text-mavi-400 hover:text-mavi-300 underline transition-colors"
                            >
                                bize bildirin
                            </Link>.
                        </p>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-3 shrink-0">
                        {/* Go to Entry Button */}
                        <Link
                            to={`/entry/${event?.id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 text-gray-200 rounded-xl transition-all text-xs font-bold font-poppins border border-white/5 hover:border-white/10 shadow-sm"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Entry'ye Git
                        </Link>

                        {/* Download Button */}
                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-4 py-2 bg-surface-800 hover:bg-surface-700 text-gray-200 rounded-xl transition-all text-xs font-bold font-poppins border border-white/5 hover:border-white/10 shadow-sm"
                        >
                            <Download className="w-3.5 h-3.5" />
                            İndir
                        </button>

                        {/* Copy Link Button */}
                        <button
                            onClick={handleCopyLink}
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-mavi-600 to-mavi-500 hover:from-mavi-500 hover:to-mavi-400 text-white rounded-xl transition-all text-xs font-bold font-poppins shadow-lg shadow-mavi-500/20 active:scale-95"
                        >
                            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? 'Kopyalandı!' : 'Linki Kopyala'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
